import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect } from 'react'
import { svc_getSnowballData } from "../services/transaction.service";
import { getSQLDateFormat, formatCurrency } from "../utilities/general-utils";
import { Link } from "react-router-dom";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import { useResponseMessages } from "../hooks/useResponseMessage";
import SnowballProgress from "../components/Snowball/SnowballProgress";

const SnowballSchedule = () => {
    const [isFetching, setIsFetching] = useState(true)
    const [showSnowballCreate, setShowSnowballCreate] = useState(false)
    const [snowballData, setSnowballData] = useState(null)
    const { getAccessTokenSilently, user } = useAuth0();
    const { addMessage } = useResponseMessages();

    const getSnowballData = async () => {
        const accessToken = await getAccessTokenSilently();
        const { data } = await svc_getSnowballData(accessToken, user.sub);

        data.forEach(element => {
            if (typeof element.data === 'string') {
                element.data = JSON.parse(element.data)
            }
        });

        if (data.length === 0) {
            setShowSnowballCreate(true)
            setIsFetching(false)
            addMessage({
                error: "You have no snowball payments. Please create a snowball payment plan."
            })
            return null
        }

        setSnowballData(data[data.length - 1].data)
        setIsFetching(false)
    }

    useEffect(() => {
        if (isFetching && !snowballData) {
            getSnowballData()
        }
    }, [isFetching, snowballData])
    
    return (
        <div style={{width: "1400px", maxWidth: "100%", margin: "0 auto"}}>
            <h1 className="text-center">Your Debt Snowball Plan</h1>

            {isFetching && <LoadingSpinner />}

            {showSnowballCreate &&
                <Link to="/tools/debt-snowball" className="btn btn-primary">Create Snowball Payment Plan</Link>
            }

            {snowballData && (
                <div style={{ marginTop: "2rem" }}>
                    {/* Enhanced Progress Display */}
                    <SnowballProgress
                        aggregateResults={snowballData}
                        showDetailedBreakdown={true}
                        showPaymentSchedules={true}
                    />
                    
                    {/* Monthly Payment Schedule */}
                    <div style={{ marginBottom: "3rem" }}>
                        <h3 style={{ textAlign: 'center', margin: '2rem 0 1rem 0', color: '#333' }}>
                            Monthly Payment Schedule
                        </h3>
                        <table
                            className="table-default"
                            style={{width: "100%", maxWidth: '1000px', border: "1px solid #d1d5db", margin: '0 auto'}}
                        >
                            <thead>
                                <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{ padding: '0.75rem', textAlign: 'center' }}>#</th>
                                    <th style={{ padding: '0.75rem', textAlign: 'center' }}>Month</th>
                                    <th style={{ padding: '0.75rem', textAlign: 'center' }}>Total Payment</th>
                                    <th style={{ padding: '0.75rem', textAlign: 'center' }}>Remaining Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {snowballData.paymentsSummary.map((payment, index) => (
                                    <tr key={index}>
                                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>{index + 1}</td>
                                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>{payment.date}</td>
                                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>{formatCurrency(payment.totalPayment)}</td>
                                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>{formatCurrency(payment.totalBalance)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Individual Account Payment Schedules - Only show if detailed data exists */}
            {snowballData?.paymentSchedules && snowballData.paymentSchedules.length > 0 && (
                <div>
                    <h3 style={{ textAlign: 'center', margin: '3rem 0 2rem 0', color: '#333' }}>
                        Individual Account Payment Schedules
                    </h3>
                    <div style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "center",
                        flexWrap: "wrap",
                        gap: "1.5rem",
                        marginBottom: "3rem"
                    }}>
                        {snowballData.paymentSchedules.map((account, index) => (
                            <div key={index} style={{ 
                                margin: "0", 
                                width: "420px", 
                                maxWidth: "100%",
                                backgroundColor: '#fff',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                padding: '1.5rem'
                            }}>
                                <h5 style={{ 
                                    textAlign: 'center', 
                                    margin: '0 0 0.5rem 0',
                                    color: '#333'
                                }}>
                                    {account.name}
                                </h5>
                                <div style={{
                                    fontSize: '1.2rem',
                                    fontWeight: 'bold',
                                    color: '#007bff',
                                    textAlign: 'center',
                                    marginBottom: '1rem'
                                }}>
                                    Starting Balance: {formatCurrency(parseFloat(
                                        account.paymentSchedule[0].balance +
                                        account.paymentSchedule[0].interestPaid +
                                        account.paymentSchedule[0].principalPaid
                                    ))}
                                </div>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ 
                                        width: "100%", 
                                        borderCollapse: 'collapse',
                                        fontSize: '0.9rem'
                                    }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                                                <th style={{ padding: '0.5rem', border: '1px solid #ddd', textAlign: 'center' }}>#</th>
                                                <th style={{ padding: '0.5rem', border: '1px solid #ddd', textAlign: 'center' }}>Date</th>
                                                <th style={{ padding: '0.5rem', border: '1px solid #ddd', textAlign: 'center' }}>Payment</th>
                                                <th style={{ padding: '0.5rem', border: '1px solid #ddd', textAlign: 'center' }}>Balance</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {account.paymentSchedule.map((payment, paymentIndex) => (
                                                <tr key={paymentIndex}>
                                                    <td style={{ padding: '0.5rem', border: '1px solid #ddd', textAlign: 'center' }}>
                                                        {paymentIndex + 1}
                                                    </td>
                                                    <td style={{ padding: '0.5rem', border: '1px solid #ddd', textAlign: 'center' }}>
                                                        {getSQLDateFormat(new Date(payment.date))}
                                                    </td>
                                                    <td style={{ padding: '0.5rem', border: '1px solid #ddd', textAlign: 'right' }}>
                                                        {formatCurrency(payment.totalPayment.toFixed(2))}
                                                    </td>
                                                    <td style={{ padding: '0.5rem', border: '1px solid #ddd', textAlign: 'right' }}>
                                                        {formatCurrency(payment.balance.toFixed(2))}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

export default SnowballSchedule